/**
 * MenuContainer Component
 * 
 * Main container that orchestrates all menu components with theme support
 */

import React, { useState, useEffect, useMemo } from 'react';
import { MenuThemeProvider } from '@/contexts/MenuThemeContext';
import { useMenuTheme } from '@/hooks/useMenuTheme';
import { MenuHeader } from './MenuHeader';
import { MenuCategoryTabs } from './MenuCategoryTabs';
import { MenuItemCard } from './MenuItemCard';
import { FloatingCart } from './FloatingCart';
import { FloatingActions } from './FloatingActions';
import { usePublicCategories } from '@/hooks/usePublicCategories';
import { usePublicMenuItems } from '@/hooks/usePublicMenuItems';
import { usePublicMenuVariants } from '@/hooks/usePublicMenuVariants';
import { Skeleton } from '@/components/ui/skeleton';
import { useTranslation } from 'react-i18next';
import type { Branch } from '@/types/index';
import type { ViewContentParams } from '@/types/metaPixel';
import { cn } from '@/lib/utils';
import { PublicMenuData } from '@/hooks/usePublicMenuFullData';

interface MenuContainerProps {
  branch: Branch;
  enableRatings?: boolean;
  enableOrdering?: boolean;
  onTrackViewContent?: (params: ViewContentParams) => void;
  isPreview?: boolean;
  showCart?: boolean;
  preloadedData?: PublicMenuData;
}

export function MenuContainer({
  branch,
  enableRatings = false,
  enableOrdering = false,
  onTrackViewContent,
  isPreview = false,
  showCart = true,
  preloadedData,
}: MenuContainerProps) {
  return (
    <MenuThemeProvider branch={branch}>
      <MenuContainerInner
        branch={branch}
        enableRatings={enableRatings}
        enableOrdering={enableOrdering}
        onTrackViewContent={onTrackViewContent}
        isPreview={isPreview}
        showCart={showCart}
        preloadedData={preloadedData}
      />
    </MenuThemeProvider>
  );
}

function MenuContainerInner({
  branch,
  enableRatings = false,
  enableOrdering = false,
  onTrackViewContent,
  isPreview = false,
  showCart = true,
  preloadedData,
}: MenuContainerProps) {
  const { i18n } = useTranslation();
  const { customization } = useMenuTheme();
  
  // Use preloaded data if available, otherwise fall back to hooks
  const { categories: fetchedCategories, loading: categoriesLoading } = usePublicCategories(preloadedData ? undefined : branch.id);
  const { menuItems: fetchedMenuItems, loading: menuItemsLoading } = usePublicMenuItems({ branchId: preloadedData ? undefined : branch.id });
  
  const categories = preloadedData?.categories || fetchedCategories;
  const menuItems = preloadedData?.menuItems || fetchedMenuItems;
  
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  // PERFORMANCE FIX: Batch fetch all variants in ONE query instead of N+1
  // See: COMPREHENSIVE_PERFORMANCE_AUDIT_2025.md - Part 6: Public Menu Analysis
  const menuItemIds = useMemo(() => menuItems.map((item: any) => item.id), [menuItems]);
  const { variantsMap: fetchedVariantsMap, loading: variantsLoading } = usePublicMenuVariants(preloadedData ? undefined : branch.id, menuItemIds);

  const variantsMap = useMemo(() => {
    if (preloadedData?.variants) {
      const map: Record<string, any[]> = {};
      preloadedData.variants.forEach((variant: any) => {
        if (!map[variant.menu_item_id]) {
          map[variant.menu_item_id] = [];
        }
        map[variant.menu_item_id].push(variant);
      });
      return map;
    }
    return fetchedVariantsMap;
  }, [preloadedData, fetchedVariantsMap]);

  const isLoading = !preloadedData && (categoriesLoading || menuItemsLoading || variantsLoading);

  // Note: We no longer automatically change the global i18n language based on branch settings.
  // The user's language preference (stored in localStorage) should be respected.
  // Branch default_language is now only used as a hint for new visitors who haven't set a preference.
  useEffect(() => {
    if (isPreview) return;

    // Only set language if user hasn't explicitly chosen one
    const userPreference = localStorage.getItem('i18nextLng');
    if (!userPreference) {
      const targetLang = branch?.default_language || 'en';
      if (i18n.language !== targetLang) {
        i18n.changeLanguage(targetLang);
      }
    }
  }, [branch?.default_language, i18n, isPreview]);

  // Set active category when categories load
  useEffect(() => {
    if (categories.length > 0 && !activeCategory) {
      setActiveCategory(categories[0].id);
    }
  }, [categories, activeCategory]);

  // Get background style
  const getMenuBgStyle = (): React.CSSProperties => {
    if (branch.menu_background_type === 'legacy' || !branch.menu_background) {
      return { backgroundColor: '#fff' };
    }
    if (branch.menu_background_type === 'none') return {};
    if (branch.menu_background_type === 'preset' || branch.menu_background_type === 'custom') {
      if (branch.menu_background.startsWith('#')) {
        return { backgroundColor: branch.menu_background };
      }
      if (branch.menu_background.startsWith('url(') || branch.menu_background.startsWith('http')) {
        return {
          backgroundImage: `url(${branch.menu_background})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        };
      }
      return { backgroundColor: branch.menu_background };
    }
    return {};
  };

  // Get layout grid classes with responsive design
  const getLayoutClass = (layout: string | null | undefined) => {
    // Get responsive grid columns from customization
    const gridCols = customization.layout.gridColumns;
    
    // In preview mode, force mobile layout (no responsive breakpoints)
    // This ensures preview matches mobile view exactly
    if (isPreview) {
      const mobileCol = `grid-cols-${gridCols.mobile}`;
      switch (layout) {
        case 'grid':
        case 'photo-grid':
        case 'gallery':
        case 'masonry':
          return `grid ${mobileCol} gap-3`;
        case 'card':
          return `grid ${mobileCol} gap-4`;
        case 'compact':
          return 'flex flex-col space-y-3';
        default: // list
          return 'space-y-3';
      }
    }
    
    // For public menu, use full responsive classes
    const mobileCol = `grid-cols-${gridCols.mobile}`;
    const tabletCol = `sm:grid-cols-${gridCols.tablet}`;
    const desktopCol = `lg:grid-cols-${gridCols.desktop}`;
    
    switch (layout) {
      case 'grid':
        return `grid ${mobileCol} ${tabletCol} ${desktopCol} gap-3 sm:gap-4`;
      case 'photo-grid':
      case 'gallery':
        return `grid ${mobileCol} ${tabletCol} gap-3 sm:gap-4`;
      case 'card':
        return `grid ${mobileCol} ${tabletCol} gap-4 sm:gap-6`;
      case 'compact':
        return 'flex flex-col space-y-3';
      case 'masonry':
        return `grid ${mobileCol} ${tabletCol} gap-3 sm:gap-4`;
      default: // list
        return 'space-y-3 sm:space-y-4';
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="max-w-3xl mx-auto bg-white" dir="rtl">
        <div className="p-4 space-y-2">
          <Skeleton className="h-8 w-1/2" />
          <Skeleton className="h-4 w-3/4" />
        </div>
        <div className="p-4 flex space-x-4 overflow-x-auto">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="w-16 h-16 rounded-full" />
          ))}
        </div>
        <div className="p-4 space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex items-center space-x-4">
              <div className="space-y-2 flex-1">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
              <Skeleton className="w-24 h-24 rounded-lg" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className="max-w-3xl mx-auto font-noto-sans-arabic h-full"
      dir="rtl"
      style={getMenuBgStyle()}
    >
        {/* Header */}
        <MenuHeader onShareClick={() => {
          // Handle share functionality
          if (navigator.share) {
            navigator.share({
              title: branch.restaurants?.name || branch.name,
              text: branch.restaurants?.description || '',
              url: window.location.href,
            });
          }
        }} />

        {/* Floating Actions (Orders & Loyalty) */}
        <FloatingActions
          branchId={branch.id}
          restaurantId={branch.restaurant_id}
          currency={branch.currency || 'USD'}
          enableOrdering={enableOrdering}
          isPreview={isPreview}
        />

        {/* Main Content */}
        <main className={cn(
          'px-4 sm:px-6 pb-32',
          branch.cover_photo_url ? 'pt-24 sm:pt-28' : 'pt-6'
        )}>
          {/* Category Tabs */}
          <MenuCategoryTabs
            categories={categories}
            activeCategory={activeCategory}
            onCategoryClick={setActiveCategory}
          />

          {/* Menu Items by Category */}
          <div className="mt-6">
            {categories.map((cat) => (
              <div
                key={cat.id}
                id={`category-section-${cat.id}`}
                className="mb-10 pt-4 -mt-4"
              >
                <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-gray-900">{cat.name}</h2>
                <div className={getLayoutClass(customization.layout.type)}>
                  {menuItems
                    .filter((item) => item.category_id === cat.id)
                    .map((item) => (
                      <MenuItemCard
                        key={item.id}
                        item={item}
                        variants={variantsMap[item.id] || []}
                        enableOrdering={enableOrdering}
                        enableRatings={enableRatings}
                        onTrackViewContent={onTrackViewContent}
                      />
                    ))}
                </div>
              </div>
            ))}
          </div>
        </main>

        {/* Floating Cart */}
        {enableOrdering && showCart && (
          <FloatingCart
            branchId={branch.id}
            currency={branch.currency || 'USD'}
            isPreview={isPreview}
          />
        )}
      </div>
  );
}
